import React, { useState, useEffect } from 'react';
import {
  CreditCard,
  Plus,
  DollarSign,
  TrendingUp,
  Package,
  Eye,
  Download,
  Settings,
  Shield,
  AlertCircle,
  Check,
  Trash2,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { paymentsService, PaymentMethod, Transaction, Sale, PaymentSettings } from '../services/paymentsService';
import AddPaymentMethodModal from './payments/AddPaymentMethodModal';

interface PaymentsTabProps {
  mode?: 'payments' | 'payout_methods';
}

const PaymentsTab: React.FC<PaymentsTabProps> = ({ mode = 'payments' }) => {
  const { user } = useAuth();
  const { select, locale } = useLanguage();
  const [activeSection, setActiveSection] = useState('overview');
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [sales, setSales] = useState<Sale[]>([]);
  const [purchases, setPurchases] = useState<Sale[]>([]);
  const [, setPaymentSettings] = useState<PaymentSettings | null>(null);
  const [earningsOverview, setEarningsOverview] = useState<Record<string, unknown> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showAddCardModal, setShowAddCardModal] = useState(false);
  const isPayoutOnly = mode === 'payout_methods';

  const [settingsForm, setSettingsForm] = useState({
    auto_payout: false,
    payout_schedule: 'weekly' as 'daily' | 'weekly' | 'monthly',
    minimum_payout_amount: 20,
    account_number: '',
    tax_id: '',
    business_name: '',
    tax_rate: 0,
    invoice_footer: '',
    payment_terms: select({ es: 'Pago debido en 30 días', en: 'Payment due within 30 days' }),
    holded_enabled: false,
    holded_api_key: '',
    holded_organization_id: '',
    holded_series_id: '',
  });

  useEffect(() => {
    loadData();
  }, [mode, user?.id]);

  const loadData = async () => {
    try {
      setLoading(true);
      if (isPayoutOnly) {
        const settings = await paymentsService.getPaymentSettings();
        setPaymentMethods([]);
        setTransactions([]);
        setSales([]);
        setPurchases([]);
        setEarningsOverview(null);
        setPaymentSettings(settings);

        if (settings) {
          setSettingsForm({
            auto_payout: settings.auto_payout,
            payout_schedule: settings.payout_schedule,
            minimum_payout_amount: settings.minimum_payout_amount,
            account_number: settings.account_number || '',
            tax_id: settings.tax_id || '',
            business_name: settings.business_name || '',
            tax_rate: settings.tax_rate,
            invoice_footer: settings.invoice_footer || '',
            payment_terms: settings.payment_terms,
            holded_enabled: settings.holded_enabled ?? false,
            holded_api_key: settings.holded_api_key || '',
            holded_organization_id: settings.holded_organization_id || '',
            holded_series_id: settings.holded_series_id || '',
          });
        }
      } else {
        const [methods, txns, salesData, purchasesData, settings, earnings] = await Promise.all([
          paymentsService.getPaymentMethods(),
          paymentsService.getTransactions({ limit: 10 }),
          paymentsService.getSales(true), // as seller
          paymentsService.getSales(false), // as buyer
          paymentsService.getPaymentSettings(),
          paymentsService.getEarningsOverview('month'),
        ]);

        setPaymentMethods(methods);
        setTransactions(txns);
        setSales(salesData);
        setPurchases(purchasesData);
        setPaymentSettings(settings);
        setEarningsOverview(earnings);

        if (settings) {
          setSettingsForm({
            auto_payout: settings.auto_payout,
            payout_schedule: settings.payout_schedule,
            minimum_payout_amount: settings.minimum_payout_amount,
            account_number: settings.account_number || '',
            tax_id: settings.tax_id || '',
            business_name: settings.business_name || '',
            tax_rate: settings.tax_rate,
            invoice_footer: settings.invoice_footer || '',
            payment_terms: settings.payment_terms,
            holded_enabled: settings.holded_enabled ?? false,
            holded_api_key: settings.holded_api_key || '',
            holded_organization_id: settings.holded_organization_id || '',
            holded_series_id: settings.holded_series_id || '',
          });
        }
      }
    } catch (err) {
      setError(select({ es: 'Error al cargar los datos de pagos', en: 'Failed to load payment data' }));
      console.error('Error loading payment data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      await paymentsService.updatePaymentSettings(settingsForm);
      setSuccess(select({ es: 'Configuración guardada correctamente', en: 'Settings saved successfully' }));
      setError('');
      loadData();
    } catch (err) {
      setError(select({ es: 'Error al guardar la configuración', en: 'Failed to save settings' }));
      console.error('Error saving payment settings:', err);
    }
  };

  const handleAddPaymentMethod = () => {
    setShowAddCardModal(true);
  };

  const handleRemovePaymentMethod = async (methodId: string) => {
    if (!window.confirm(select({ es: '¿Estás seguro de que quieres eliminar este método de pago?', en: 'Are you sure you want to delete this payment method?' }))) {
      return;
    }

    try {
      await paymentsService.removePaymentMethod(methodId);
      setPaymentMethods(prev => prev.filter(method => method.id !== methodId));
      setSuccess(select({ es: 'Método de pago eliminado correctamente', en: 'Payment method removed successfully' }));
    } catch (err) {
      setError(select({ es: 'Error al eliminar el método de pago', en: 'Failed to remove payment method' }));
      console.error('Error removing payment method:', err);
    }
  };

  const formatPrice = (amount: number) => {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getTransactionStatusColor = (status: string) => {
    switch (status) {
      case 'succeeded': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'processing': return 'bg-blue-100 text-blue-800';
      case 'failed': return 'bg-red-100 text-red-800';
      case 'cancelled': return 'bg-gray-100 text-gray-800';
      case 'refunded': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getSaleStatusColor = (status: string) => {
    switch (status) {
      case 'delivered': return 'bg-green-100 text-green-800';
      case 'shipped': return 'bg-blue-100 text-blue-800';
      case 'processing': return 'bg-yellow-100 text-yellow-800';
      case 'pending': return 'bg-orange-100 text-orange-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const transactionTypeLabels: Record<string, { es: string; en: string }> = {
    sale: { es: 'Venta', en: 'Sale' },
    refund: { es: 'Reembolso', en: 'Refund' },
    payout: { es: 'Pago', en: 'Payout' },
    commission: { es: 'Comisión', en: 'Commission' },
    fee: { es: 'Tarifa', en: 'Fee' },
  };

  const transactionStatusLabels: Record<string, { es: string; en: string }> = {
    succeeded: { es: 'Completado', en: 'Completed' },
    pending: { es: 'Pendiente', en: 'Pending' },
    processing: { es: 'Procesando', en: 'Processing' },
    failed: { es: 'Fallido', en: 'Failed' },
    cancelled: { es: 'Cancelado', en: 'Cancelled' },
    refunded: { es: 'Reembolsado', en: 'Refunded' },
  };

  const saleStatusLabels: Record<string, { es: string; en: string }> = {
    delivered: { es: 'Entregado', en: 'Delivered' },
    shipped: { es: 'Enviado', en: 'Shipped' },
    processing: { es: 'Procesando', en: 'Processing' },
    pending: { es: 'Pendiente', en: 'Pending' },
    cancelled: { es: 'Cancelado', en: 'Cancelled' },
    confirmed: { es: 'Confirmado', en: 'Confirmed' },
  };

  const deliveryMethodLabels: Record<string, { es: string; en: string }> = {
    digital: { es: 'Digital', en: 'Digital' },
    physical: { es: 'Físico', en: 'Physical' },
    both: { es: 'Ambos', en: 'Both' },
  };

  const getTransactionTypeLabel = (type: string) => select(transactionTypeLabels[type] || { es: type, en: type });
  const getTransactionStatusLabel = (status: string) => select(transactionStatusLabels[status] || { es: status, en: status });
  const getSaleStatusLabel = (status: string) => select(saleStatusLabels[status] || { es: status, en: status });
  const getDeliveryMethodLabel = (method: string) => select(deliveryMethodLabels[method] || { es: method, en: method });

  const calculateUnlockProgress = (createdAt: string) => {
    const startDate = new Date(createdAt).getTime();
    const now = Date.now();
    const unlockDays = 20;
    const msPerDay = 24 * 60 * 60 * 1000;
    const daysPassed = (now - startDate) / msPerDay;
    
    const progress = Math.min(100, Math.max(0, (daysPassed / unlockDays) * 100));
    const daysRemaining = Math.max(0, Math.ceil(unlockDays - daysPassed));
    
    return { progress, daysRemaining };
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-6 bg-gray-200 rounded w-1/4"></div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="bg-gray-200 h-24 rounded-lg"></div>
          ))}
        </div>
      </div>
    );
  }

  const payoutMethodsSection = (
    <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-6">
      <div>
        <h5 className="font-medium text-gray-900 mb-4">
          {select({ es: 'Métodos de cobro', en: 'Payout methods' })}
        </h5>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {select({ es: 'Frecuencia de cobros', en: 'Payout frequency' })}
            </label>
            <select
              value={settingsForm.payout_schedule}
              onChange={(e) => setSettingsForm(prev => ({...prev, payout_schedule: e.target.value as 'daily' | 'weekly' | 'monthly'}))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="daily">{select({ es: 'Diario', en: 'Daily' })}</option>
              <option value="weekly">{select({ es: 'Semanal (recomendado)', en: 'Weekly (recommended)' })}</option>
              <option value="monthly">{select({ es: 'Mensual', en: 'Monthly' })}</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {select({ es: 'Monto mínimo para cobro (EUR)', en: 'Minimum payout amount (EUR)' })}
            </label>
            <input
              type="number"
              min="5"
              step="0.01"
              value={settingsForm.minimum_payout_amount}
              onChange={(e) => setSettingsForm(prev => ({...prev, minimum_payout_amount: parseFloat(e.target.value) || 20}))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
        </div>

        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {select({ es: 'Número de cuenta', en: 'Account Number' })}
          </label>
          <input
            type="text"
            value={settingsForm.account_number}
            onChange={(e) => setSettingsForm(prev => ({...prev, account_number: e.target.value}))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            placeholder="ESXX XXXX XXXX XXXX XXXX XXXX"
          />
        </div>

        <div className="mt-4">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={settingsForm.auto_payout}
              onChange={(e) => setSettingsForm(prev => ({...prev, auto_payout: e.target.checked}))}
              className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
            />
            <span className="text-sm text-gray-700">
              {select({ es: 'Activar cobros automáticos', en: 'Enable automatic payouts' })}
            </span>
          </label>
          <p className="text-xs text-gray-500 ml-6">
            {select({
              es: 'Los cobros se procesarán automáticamente según la frecuencia seleccionada',
              en: 'Payouts will run automatically based on the selected frequency',
            })}
          </p>
        </div>
      </div>

      <div>
        <h5 className="font-medium text-gray-900 mb-4">
          {select({ es: 'Información fiscal', en: 'Tax information' })}
        </h5>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {select({ es: 'NIF/CIF', en: 'Tax ID' })}
            </label>
            <input
              type="text"
              value={settingsForm.tax_id}
              onChange={(e) => setSettingsForm(prev => ({...prev, tax_id: e.target.value}))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder={select({ es: '12345678Z', en: '12345678Z' })}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {select({ es: 'Nombre del negocio (opcional)', en: 'Business name (optional)' })}
            </label>
            <input
              type="text"
              value={settingsForm.business_name}
              onChange={(e) => setSettingsForm(prev => ({...prev, business_name: e.target.value}))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder={select({ es: 'Mi Estudio de Arte S.L.', en: 'My Art Studio LLC' })}
            />
          </div>
        </div>

        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {select({ es: 'Tasa de impuestos (%)', en: 'Tax rate (%)' })}
          </label>
          <input
            type="number"
            min="0"
            max="100"
            step="0.01"
            value={settingsForm.tax_rate}
            onChange={(e) => setSettingsForm(prev => ({...prev, tax_rate: parseFloat(e.target.value) || 0}))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            placeholder="21.00"
          />
        </div>
      </div>

      <button
        onClick={handleSaveSettings}
        className="w-full px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center justify-center gap-2"
      >
        <Settings size={16} />
        {select({ es: 'Guardar configuración', en: 'Save settings' })}
      </button>
    </div>
  );

  if (isPayoutOnly) {
    return (
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            {select({ es: 'Métodos de cobro', en: 'Payout methods' })}
          </h3>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
              <AlertCircle size={16} />
              {error}
            </div>
          )}

          {success && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm flex items-center gap-2">
              <Check size={16} />
              {success}
            </div>
          )}

          {payoutMethodsSection}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          {select({ es: 'Pagos y Facturación', en: 'Payments & Billing' })}
        </h3>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
            <AlertCircle size={16} />
            {error}
          </div>
        )}

        {success && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm flex items-center gap-2">
            <Check size={16} />
            {success}
          </div>
        )}

        {/* Stripe Integration Notice */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6 mb-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-blue-600 rounded-xl">
              <Shield className="text-white" size={24} />
            </div>
            <div>
              <h4 className="text-lg font-bold text-gray-900 mb-2">
                {select({ es: '🔒 Pagos Seguros con Stripe', en: '🔒 Secure Payments with Stripe' })}
              </h4>
              <p className="text-gray-700 mb-3">
                {select({
                  es: 'Tus pagos están completamente integrados con',
                  en: 'Your payments are fully integrated with',
                })}{' '}
                <strong>Stripe</strong>,{' '}
                {select({
                  es: 'la plataforma de pagos más segura del mundo. Nunca almacenamos datos de tarjetas por tu seguridad.',
                  en: 'the most secure payment platform worldwide. We never store card details for your safety.',
                })}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span>
                    <strong>PCI DSS Compliant</strong> - {select({ es: 'Máxima seguridad', en: 'Maximum security' })}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  <span>
                    <strong>SSL 256-bit</strong> - {select({ es: 'Encriptación bancaria', en: 'Bank-grade encryption' })}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                  <span>
                    <strong>3D Secure</strong> - {select({ es: 'Verificación adicional', en: 'Additional verification' })}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Section Navigation */}
        <div className="flex space-x-1 bg-gray-100 rounded-lg p-1 mb-6">
          {[
            { id: 'overview', label: select({ es: 'Resumen', en: 'Overview' }), icon: TrendingUp },
            { id: 'methods', label: select({ es: 'Métodos de Pago', en: 'Payment Methods' }), icon: CreditCard },
            { id: 'sales', label: select({ es: 'Ventas', en: 'Sales' }), icon: DollarSign },
            { id: 'purchases', label: select({ es: 'Compras', en: 'Purchases' }), icon: Package },
          ].map((section) => {
            const Icon = section.icon;
            return (
              <button
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                  activeSection === section.id
                    ? 'bg-white text-purple-700 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Icon size={16} />
                <span className="hidden md:inline">{section.label}</span>
              </button>
            );
          })}
        </div>

        {/* Overview Section */}
        {activeSection === 'overview' && earningsOverview && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-gradient-to-r from-green-500 to-emerald-500 rounded-lg p-6 text-white">
                <div className="flex items-center gap-3">
                  <DollarSign size={24} />
                  <div>
                    <p className="text-green-100">{select({ es: 'Ganancias del mes', en: 'Earnings this month' })}</p>
                    <p className="text-2xl font-bold">{formatPrice(earningsOverview.totalEarnings)}</p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-blue-500 to-cyan-500 rounded-lg p-6 text-white">
                <div className="flex items-center gap-3">
                  <Package size={24} />
                  <div>
                    <p className="text-blue-100">{select({ es: 'Ventas realizadas', en: 'Completed sales' })}</p>
                    <p className="text-2xl font-bold">{earningsOverview.salesCount}</p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg p-6 text-white">
                <div className="flex items-center gap-3">
                  <TrendingUp size={24} />
                  <div>
                    <p className="text-purple-100">{select({ es: 'Valor promedio', en: 'Average order value' })}</p>
                    <p className="text-2xl font-bold">{formatPrice(earningsOverview.averageSaleValue)}</p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-orange-500 to-red-500 rounded-lg p-6 text-white">
                <div className="flex items-center gap-3">
                  <CreditCard size={24} />
                  <div>
                    <p className="text-orange-100">{select({ es: 'Comisiones', en: 'Commissions' })}</p>
                    <p className="text-2xl font-bold">{formatPrice(earningsOverview.totalCommissions)}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Transactions */}
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h4 className="font-semibold text-gray-900 mb-4">
                {select({ es: 'Transacciones recientes', en: 'Recent transactions' })}
              </h4>
              <div className="space-y-3">
                {transactions.slice(0, 5).map((transaction) => (
                  <div key={transaction.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                        <DollarSign className="text-purple-600" size={16} />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">
                          {getTransactionTypeLabel(transaction.type)}
                        </p>
                        <p className="text-sm text-gray-500">
                          {transaction.description || formatDate(transaction.created_at)}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-gray-900">{formatPrice(transaction.net_amount)}</p>
                      <span className={`inline-block px-2 py-1 text-xs rounded-full ${getTransactionStatusColor(transaction.status)}`}>
                        {getTransactionStatusLabel(transaction.status)}
                      </span>
                    </div>
                  </div>
                ))}

                {transactions.length === 0 && (
                  <div className="text-center py-8">
                    <TrendingUp size={48} className="mx-auto text-gray-300 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      {select({ es: 'No hay transacciones', en: 'No transactions yet' })}
                    </h3>
                    <p className="text-gray-500">
                      {select({
                        es: 'Cuando realices tu primera venta o compra aparecerá aquí',
                        en: 'Your first sale or purchase will show up here',
                      })}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Payment Methods Section */}
        {activeSection === 'methods' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h4 className="font-semibold text-gray-900">
                {select({ es: 'Métodos de pago', en: 'Payment methods' })}
              </h4>
              <button
                onClick={handleAddPaymentMethod}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                <Plus size={16} />
                {select({ es: 'Añadir método', en: 'Add method' })}
              </button>
            </div>

            {/* Payment Methods List */}
            <div className="space-y-4">
              {paymentMethods.map((method) => (
                <div key={method.id} className="bg-white border border-gray-200 rounded-lg p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-8 bg-blue-600 rounded flex items-center justify-center text-white font-bold text-xs">
                        {method.brand?.toUpperCase() || 'CARD'}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">•••• •••• •••• {method.last_four}</p>
                        <p className="text-sm text-gray-500 flex flex-wrap items-center gap-2">
                          <span>{getPaymentMethodLabel(method.type)}</span>
                          {method.exp_month && method.exp_year && (
                            <span className="text-xs text-gray-400">
                              {select({ es: 'Caduca', en: 'Exp' })}:{' '}
                              {String(method.exp_month).padStart(2, '0')}/{String(method.exp_year).slice(-2)}
                            </span>
                          )}
                        </p>
                        {method.billing_name && (
                          <p className="text-xs text-gray-400">
                            {select({ es: 'Titular:', en: 'Cardholder:' })} {method.billing_name}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      {method.is_default && (
                        <span className="px-3 py-1 bg-green-100 text-green-700 text-sm rounded-full font-medium">
                          {select({ es: 'Principal', en: 'Primary' })}
                        </span>
                      )}
                      <button
                        onClick={() => handleRemovePaymentMethod(method.id)}
                        className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                        title={select({ es: 'Eliminar método de pago', en: 'Remove payment method' })}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}

              {paymentMethods.length === 0 && (
                <div className="text-center py-12">
                  <CreditCard size={48} className="mx-auto text-gray-300 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    {select({ es: 'No hay métodos de pago', en: 'No payment methods yet' })}
                  </h3>
                  <p className="text-gray-500 mb-6">
                    {select({
                      es: 'Añade un método de pago para comprar arte y gestionar tus ventas',
                      en: 'Add a payment method to purchase art and manage your sales',
                    })}
                  </p>
                  <button
                    onClick={handleAddPaymentMethod}
                    className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                  >
                    {select({ es: 'Añadir primer método', en: 'Add first method' })}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Sales Section */}
        {activeSection === 'sales' && (
          <div className="space-y-6">
            <h4 className="font-semibold text-gray-900">
              {select({ es: 'Mis ventas', en: 'My sales' })}
            </h4>
            
            <div className="space-y-4">
              {sales.map((sale) => {
                const { progress, daysRemaining } = calculateUnlockProgress(sale.created_at);
                
                return (
                <div key={sale.id} className="bg-white border border-gray-200 rounded-lg p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-start gap-4">
                      {sale.post?.media_files?.[0] && (
                        <img
                          src={sale.post.media_files[0].url}
                          alt={sale.post.title}
                          className="w-16 h-16 object-cover rounded-lg"
                        />
                      )}
                      <div>
                        <h5 className="font-semibold text-gray-900">{sale.post?.title}</h5>
                        <p className="text-sm text-gray-500">
                          {select({ es: 'Comprado por', en: 'Purchased by' })} @{sale.buyer?.username}
                        </p>
                        <p className="text-sm text-gray-500">{formatDate(sale.created_at)}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-gray-900">{formatPrice(sale.sale_price)}</p>
                      <p className="text-sm text-gray-500">
                        {select({ es: 'Ganancias:', en: 'Earnings:' })} {formatPrice(sale.artist_earnings)}
                      </p>
                      <span className={`inline-block px-2 py-1 text-xs rounded-full mt-1 ${getSaleStatusColor(sale.delivery_status)}`}>
                        {getSaleStatusLabel(sale.delivery_status)}
                      </span>
                    </div>
                  </div>
                  
                  {/* Fund Release Progress */}
                  <div className="mt-4 mb-4">
                    <div className="flex justify-between text-xs text-gray-500 mb-1">
                      <span>{select({ es: 'Desbloqueo de fondos', en: 'Funds unlock' })}</span>
                      <span>
                        {daysRemaining > 0 
                          ? select({ es: `${daysRemaining} días restantes`, en: `${daysRemaining} days remaining` })
                          : select({ es: 'Disponible', en: 'Available' })
                        }
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2.5">
                      <div 
                        className={`h-2.5 rounded-full ${daysRemaining === 0 ? 'bg-green-600' : 'bg-yellow-400'}`} 
                        style={{ width: `${progress}%` }}
                      ></div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-600">
                      <span>
                        {select({ es: 'Método:', en: 'Method:' })} {getDeliveryMethodLabel(sale.delivery_method)}
                      </span>
                      <span className="ml-4">
                        {select({ es: 'Comisión:', en: 'Commission:' })} {formatPrice(sale.commission_amount)}
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <button className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 transition-colors">
                        <Eye size={14} className="inline mr-1" />
                        {select({ es: 'Ver detalles', en: 'View details' })}
                      </button>
                      <button className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 transition-colors">
                        <Download size={14} className="inline mr-1" />
                        {select({ es: 'Factura', en: 'Invoice' })}
                      </button>
                    </div>
                  </div>
                </div>
              );
              })}

              {sales.length === 0 && (
                <div className="text-center py-8">
                  <Package size={48} className="mx-auto text-gray-300 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    {select({ es: 'No hay ventas aún', en: 'No sales yet' })}
                  </h3>
                  <p className="text-gray-500">
                    {select({
                      es: 'Cuando vendas tu primera obra aparecerá aquí',
                      en: 'Your first sale will appear here',
                    })}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Purchases Section */}
        {activeSection === 'purchases' && (
          <div className="space-y-6">
            <h4 className="font-semibold text-gray-900">
              {select({ es: 'Mis compras', en: 'My purchases' })}
            </h4>
            
            <div className="space-y-4">
              {purchases.map((purchase) => (
                <div key={purchase.id} className="bg-white border border-gray-200 rounded-lg p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-start gap-4">
                      {purchase.post?.media_files?.[0] && (
                        <img
                          src={purchase.post.media_files[0].url}
                          alt={purchase.post.title}
                          className="w-16 h-16 object-cover rounded-lg"
                        />
                      )}
                      <div>
                        <h5 className="font-semibold text-gray-900">{purchase.post?.title}</h5>
                        <p className="text-sm text-gray-500">
                          {select({ es: 'Por', en: 'By' })} @{purchase.seller?.username}
                        </p>
                        <p className="text-sm text-gray-500">{formatDate(purchase.created_at)}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-gray-900">{formatPrice(purchase.sale_price)}</p>
                      <span className={`inline-block px-2 py-1 text-xs rounded-full mt-1 ${getSaleStatusColor(purchase.delivery_status)}`}>
                        {getSaleStatusLabel(purchase.delivery_status)}
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-600">
                      <span>
                        {select({ es: 'Método:', en: 'Method:' })} {getDeliveryMethodLabel(purchase.delivery_method)}
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <button className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 transition-colors">
                        <Eye size={14} className="inline mr-1" />
                        {select({ es: 'Ver detalles', en: 'View details' })}
                      </button>
                      <button className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 transition-colors">
                        <Download size={14} className="inline mr-1" />
                        {select({ es: 'Recibo', en: 'Receipt' })}
                      </button>
                    </div>
                  </div>
                </div>
              ))}

              {purchases.length === 0 && (
                <div className="text-center py-8">
                  <Package size={48} className="mx-auto text-gray-300 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    {select({ es: 'No hay compras aún', en: 'No purchases yet' })}
                  </h3>
                  <p className="text-gray-500">
                    {select({
                      es: 'Cuando compres tu primera obra aparecerá aquí',
                      en: 'Your first purchase will appear here',
                    })}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

      </div>

      <AddPaymentMethodModal
        isOpen={showAddCardModal}
        onClose={() => setShowAddCardModal(false)}
      />
    </div>
  );
};

export default PaymentsTab;
